import { isVorgangLockExpired } from '@core/domain';
import { useEffect, useCallback, useRef, useState } from 'react';
import {
  useLockVorgang,
  useUnlockVorgang,
} from '@/adapter/opm-cockpit-backend';
import type { VorgangDetail } from '@/types';

interface UseVorgangLockingProps {
  vorgang: VorgangDetail | undefined;
  currentUser: string;
  onLockSuccess?: () => void;
  onLockError?: (error: Error) => void;
  onUnlockSuccess?: () => void;
  onUnlockError?: (error: Error) => void;
  onLockStatusChange?: (isLockedByOther: boolean, lockedBy?: string) => void;
  refetch?: () => Promise<unknown>;
  autoLock?: boolean;
}

interface UseVorgangLockingReturn {
  isLockedByCurrentUser: boolean;
  isLockedByOtherUser: boolean;
  lockedBy: string | undefined;
  lockVorgang: () => Promise<void>;
  unlockVorgang: () => Promise<void>;
  isLocking: boolean;
  isUnlocking: boolean;
}

export const useVorgangLocking = ({
  vorgang,
  currentUser,
  onLockSuccess,
  onLockError,
  onUnlockSuccess,
  onUnlockError,
  onLockStatusChange,
  refetch,
  autoLock = true,
}: UseVorgangLockingProps): UseVorgangLockingReturn => {
  const lockMutation = useLockVorgang();
  const unlockMutation = useUnlockVorgang();
  const [lockCheckInterval, setLockCheckInterval] =
    useState<NodeJS.Timeout | null>(null);

  // Store current values in ref for cleanup
  const currentValuesRef = useRef({
    vorgang,
    currentUser,
    unlockMutation,
    autoLock,
  });

  // Update ref whenever values change
  useEffect(() => {
    currentValuesRef.current = {
      vorgang,
      currentUser,
      unlockMutation,
      autoLock,
    };
  }, [vorgang, currentUser, unlockMutation, autoLock]);

  const isLockedByCurrentUser =
    vorgang?.lockedBy === currentUser &&
    !isVorgangLockExpired(vorgang.lockedAt);

  const isLockedByOtherUser =
    vorgang?.lockedBy &&
    vorgang.lockedBy !== currentUser &&
    !isVorgangLockExpired(vorgang.lockedAt);

  const checkLockStatus = useCallback(async () => {
    if (!vorgang?.id || !refetch || vorgang.deletedAt) return;

    try {
      const result = await refetch();
      const updatedVorgang = (result as { data: VorgangDetail }).data;
      const updatedLockedBy = updatedVorgang?.lockedBy;

      if (updatedLockedBy && updatedLockedBy !== currentUser) {
        onLockStatusChange?.(true, updatedLockedBy);
      } else {
        onLockStatusChange?.(false);
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error checking lock status:', err);
    }
  }, [
    vorgang?.id,
    vorgang?.deletedAt,
    currentUser,
    refetch,
    onLockStatusChange,
  ]);

  // Set up lock status checking interval
  useEffect(() => {
    if (vorgang?.id) {
      const interval = setInterval(checkLockStatus, 30000); // Check every 30 seconds
      setLockCheckInterval(interval);

      return () => {
        clearInterval(interval);
      };
    }
  }, [vorgang?.id, checkLockStatus]);

  // Cleanup: release lock when component unmounts
  useEffect(() => {
    return () => {
      if (lockCheckInterval) {
        clearInterval(lockCheckInterval);
      }

      // Release lock when leaving page using current values from ref
      const {
        vorgang: currentVorgang,
        currentUser: user,
        unlockMutation: unlock,
        autoLock: isAutoLockEnabled,
      } = currentValuesRef.current;

      // Only unlock if vorgang is not deleted
      if (
        isAutoLockEnabled &&
        currentVorgang?.id &&
        currentVorgang.lockedBy === user &&
        !currentVorgang.deletedAt
      ) {
        unlock.mutate({ id: currentVorgang.id });
      }
    };
  }, []); // Empty dependency array - only cleanup on unmount

  const lockVorgang = useCallback(async () => {
    if (!vorgang?.id || lockMutation.isPending || vorgang.deletedAt) return;

    lockMutation.mutate(
      { id: vorgang.id, user: currentUser },
      {
        onSuccess: onLockSuccess,
        onError: onLockError,
      },
    );
  }, [vorgang?.id, vorgang?.deletedAt, currentUser, lockMutation]);

  const unlockVorgang = useCallback(async () => {
    if (!autoLock || !vorgang?.id || unlockMutation.isPending) return;

    unlockMutation.mutate(
      { id: vorgang.id },
      {
        onSuccess: onUnlockSuccess,
        onError: onUnlockError,
      },
    );
  }, [autoLock, vorgang?.id, vorgang?.deletedAt, unlockMutation]);

  return {
    isLockedByCurrentUser,
    isLockedByOtherUser,
    lockedBy: vorgang?.lockedBy,
    lockVorgang,
    unlockVorgang,
    isLocking: lockMutation.isPending,
    isUnlocking: unlockMutation.isPending,
  };
};
